import { createContext, useCallback, useRef } from 'react'

import Cookie from 'js-cookie'
import pkceChallenge from 'pkce-challenge'
import { withRouter } from 'react-router-dom/cjs/react-router-dom'

import useLocalStorageOld from '../hooks/UseLocalStorage'
import appConfig from '../config'
import { Routes } from '../routes'
import { loginSSO } from '../utils/api/auth'
import { Cookies } from '../utils/Cookie'

export const StorageKeys = Object.freeze({
  CODE_VERIFIER: 'code_verifier',
  ACCOUNT_ID: 'account_id'
})

export const AuthContext = createContext()

const AuthProvider = ({ children, history }) => {
  const [codeVerifier, setCodeVerifier] = useLocalStorageOld(StorageKeys.CODE_VERIFIER, null)
  const [, setAccountId] = useLocalStorageOld(StorageKeys.ACCOUNT_ID, null)
  // An SSO authorization code is single-use. AuthCallback can remount (e.g. after the terms have
  // been accepted) with the same code still in the URL; this ref prevents replaying it.
  const exchangedCodesRef = useRef(new Set())
  /**
   * After Auth callback, exchange the authorization code for a token
   */
  const authCallback = useCallback(
    /**
     * @param {string} authorizationCode
     * @param {function} onGetUserContextAndData
     * @param {function} onAfterLogin
     * @param {?number} profileId the chosen profile (step 2 of the multi-profile login)
     * @returns {Promise<?Array>} the list of profiles when the account holds several of them
     *   (step 1), undefined otherwise (login completed)
     */
    async (authorizationCode, onGetUserContextAndData, onAfterLogin, profileId = null) => {
      // Never replay an exchange that already happened (it would 401 with
      // auth.invalid_or_expired_code). The key includes the profile: step 2 of the multi-profile
      // login exchanges the same code again, with profile-id this time.
      const exchangeKey = `${authorizationCode}:${profileId ?? ''}`
      if (exchangedCodesRef.current.has(exchangeKey)) {
        return
      }
      exchangedCodesRef.current.add(exchangeKey)

      const data = await loginSSO(authorizationCode, codeVerifier, profileId)

      if (!data) {
        throw new Error('Error getting token')
      }

      if (Array.isArray(data.profiles)) {
        // Step 1: the account is authenticated but has zero or several active profiles — no token
        // yet, the UI shows the profile selector then calls back with the chosen profile.
        return data.profiles
      }

      setAccountId(data.accountId)

      // Persist the token (same format as the plain local login): on a page reload,
      // App.componentDidMount restores the session from this cookie instead of going back to login.
      Cookie.set(Cookies.TOKEN, JSON.stringify(data.token), { expires: 7 })

      onGetUserContextAndData(data.token).then(() => {
        onAfterLogin()
      })
    },
    []
  )

  const createCodeChallenge = async () => {
    const challenge = await pkceChallenge()

    setCodeVerifier(challenge.code_verifier)

    return challenge.code_challenge
  }

  const redirectToSSOLoginPage = async (email = null) => {
    const challenge = await createCodeChallenge()

    let url = `${appConfig.opalAuthServeurWebapp}?${appConfig.redirectUrl}&code_challenge=${challenge}`

    if (email) {
      // encodeURIComponent so that the '+' of an email (e.g. louis+test@…) is not decoded as a
      // space on the SSO side.
      url += `&email=${encodeURIComponent(email)}`
    }

    window.location.href = url
  }

  const navigateToLoginPage = async () => {
    history.push(Routes.LOGIN)
  }

  return (
    <AuthContext.Provider
      value={{
        authCallback,
        createCodeChallenge,
        redirectToSSOLoginPage,
        navigateToLoginPage
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default withRouter(AuthProvider)
